import React from 'react'
import {OrderWindowStatus, Slot} from '../containers/slotManagement/types'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {FieldArrayWithId} from 'react-hook-form'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {
  HaulerInfo,
  OrderRequest,
  OrdersFormValidityState,
  QuantityType
} from '../../declarations/types'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {Box, CircularProgress, Grid, Paper} from '@mui/material'
import {SplitLoadOrderSummarySection} from '../containers/SplitLoadOrderSummarySection'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {OrderSummarySection} from '../containers/OrderSummarySection'
import {MaterialOrderEditor} from '../containers/MaterialOrderEditor'
import {partial} from 'lodash'

interface CollectSidebarProps {
  // State
  placeOrderStatus: boolean
  noValidMaterial: boolean
  isSplitLoadMode: boolean
  isAllOrdersValid: boolean
  activePopup: string | undefined
  setActivePopup: React.Dispatch<React.SetStateAction<any>>

  // Slot management
  shouldRedirectToSummaryPage: boolean
  canShowExpiredWindowAlert: boolean
  setOrderWindowStatus: (status: OrderWindowStatus) => void
  slots: Slot[]
  setSlots: React.Dispatch<React.SetStateAction<Slot[]>>

  // Material editor
  materialEditorController: ReturnType<typeof useMaterialEditorController>
  selectedMaterialIndex: number
  orderFields: FieldArrayWithId<BulkOrderFormValues, 'orders'>[]
  orders: OrderRequest[]
  isCustomerReferenceSame: boolean
  isSlotsManagementEnabled: boolean
  editorActivationTimeStamp: string | undefined
  savedBomKeysFromParentForm: Record<number, string | undefined> | undefined
  selectedSplitLoadMaterialsFromParentForm: Record<number, any> | undefined
  ordersFormValidity: OrdersFormValidityState[]
  setOrdersFormValidity: (val: OrdersFormValidityState[]) => void

  // Callbacks from useFieldHandlers
  onHaulerInfoChange: (index: number, haulerInfo?: HaulerInfo) => void
  onCustomerReferenceChange: (index: number, value: string) => void
  onQuantityChange: (index: number, quantity: number) => void
  onDriverInstructionsChange: (index: number, value: string) => void
  onDispatchNotesChange: (index: number, value: string) => void
  onBomKeyChange: (index: number, value: string | undefined) => void
  onMaterialChange: (index: number, value: any) => void
  onSplitLoadMaterialsSelected: (index: number, value: any) => void
  handleStatusChange: (hasChanges: boolean) => void
  removeCollection: (index: number) => void
  handleSummarySectionEditButtonClick: (index: number) => void

  // Form handlers
  formChangeHandlers: ReturnType<typeof useBulkOrderFormChangeHandlerWithMethods>
  customerId?: string
  quantityType: QuantityType

  // UI
  classes: any
}

export const CollectSidebar = (props: CollectSidebarProps) => {
  const {
    classes,
    placeOrderStatus,
    materialEditorController,
    noValidMaterial,
    shouldRedirectToSummaryPage,
    isSplitLoadMode,
    customerId,
    quantityType,
    setActivePopup,
    formChangeHandlers,
    handleSummarySectionEditButtonClick,
    isAllOrdersValid,
    setOrdersFormValidity,
    canShowExpiredWindowAlert,
    orderFields,
    selectedMaterialIndex,
    orders,
    isCustomerReferenceSame,
    onSplitLoadMaterialsSelected,
    selectedSplitLoadMaterialsFromParentForm,
    onHaulerInfoChange,
    onCustomerReferenceChange,
    onQuantityChange,
    onDriverInstructionsChange,
    onDispatchNotesChange,
    savedBomKeysFromParentForm,
    onMaterialChange,
    onBomKeyChange,
    removeCollection,
    slots,
    editorActivationTimeStamp,
    isSlotsManagementEnabled,
    handleStatusChange,
    ordersFormValidity,
    setOrderWindowStatus,
    setSlots
  } = props

  return (
    <Grid item xs={12} md={4} style={{paddingLeft: 16, paddingRight: 0}}>
      <Paper className={classes.paperStyle}>
        <div className={classes.stickyContainer}>
          {placeOrderStatus ? (
            <Box display="flex" alignItems="center" flexDirection="column">
              <CircularProgress size={24} color="inherit" />
            </Box>
          ) : materialEditorController.activeMaterialForm === undefined ||
            noValidMaterial ||
            shouldRedirectToSummaryPage ? (
            isSplitLoadMode ? (
              <SplitLoadOrderSummarySection
                shippingType={ShippingType.COLLECT}
                customerId={customerId}
                quantityType={quantityType}
                onCloseSummaryBox={() => setActivePopup(undefined)}
                onCustomerRefChange={formChangeHandlers.onGlobalCustomerRefChange}
                onEmailCcChange={formChangeHandlers.onGlobalEmailCcChange}
                onEditItem={handleSummarySectionEditButtonClick}
                isAllOrdersValid={isAllOrdersValid}
                onRevalidateForm={(invalidOrders) => {
                  setOrdersFormValidity(invalidOrders)
                }}
                canShowExpiredWindowAlert={canShowExpiredWindowAlert}
              />
            ) : (
              <OrderSummarySection
                shippingType={ShippingType.COLLECT}
                customerId={customerId}
                quantityType={quantityType}
                onCloseSummaryBox={() => setActivePopup(undefined)}
                onCustomerRefChange={formChangeHandlers.onGlobalCustomerRefChange}
                onEmailCcChange={formChangeHandlers.onGlobalEmailCcChange}
                onEditItem={handleSummarySectionEditButtonClick}
                isAllOrdersValid={isAllOrdersValid}
                onRevalidateForm={(invalidOrders) => {
                  setOrdersFormValidity(invalidOrders)
                }}
                canShowExpiredWindowAlert={canShowExpiredWindowAlert}
              />
            )
          ) : (
            orderFields.length > 0 &&
            selectedMaterialIndex > -1 &&
            selectedMaterialIndex < orderFields.length && (
              <MaterialOrderEditor
                key={materialEditorController.activeMaterialForm}
                orderPayload={orderFields[selectedMaterialIndex].payload}
                canDeleteMaterial={orders.length > 1}
                checkValidityBySlots={false}
                index={selectedMaterialIndex}
                shippingType={ShippingType.COLLECT}
                activeItem={materialEditorController.activeSection}
                editorMode={materialEditorController.activeMode}
                showPoTab={!isCustomerReferenceSame}
                isCustomerReferenceSame={isCustomerReferenceSame}
                isSplitLoad={isSplitLoadMode}
                savedSplitLoadData={
                  selectedSplitLoadMaterialsFromParentForm?.[selectedMaterialIndex]
                }
                closeActiveItem={() => materialEditorController.setActiveOrderBox(undefined)}
                onChange={() =>
                  materialEditorController.activeMaterialForm &&
                  materialEditorController.notifySuccessfullySaved(
                    materialEditorController.activeMaterialForm
                  )
                }
                onDateChange={partial(formChangeHandlers.onDateChange, selectedMaterialIndex)}
                onTimeChange={partial(formChangeHandlers.onTimeChange, selectedMaterialIndex)}
                onSlotChange={partial(formChangeHandlers.onSlotChange, selectedMaterialIndex)}
                onHaulerInfoChange={(haulerInfo) =>
                  onHaulerInfoChange(selectedMaterialIndex, haulerInfo)
                }
                onCustomerReferenceChange={(customerReference) =>
                  onCustomerReferenceChange(selectedMaterialIndex, customerReference)
                }
                onQuantityChange={(quantity) => onQuantityChange(selectedMaterialIndex, quantity)}
                onDriverInstructionsChange={(driverInstructions) =>
                  onDriverInstructionsChange(selectedMaterialIndex, driverInstructions)
                }
                onDispatchNotesChange={(dispatchNotes) =>
                  onDispatchNotesChange(selectedMaterialIndex, dispatchNotes)
                }
                savedBomKey={
                  savedBomKeysFromParentForm?.[selectedMaterialIndex] as string | undefined
                }
                onBomKeyChange={(bomKey: string | undefined) =>
                  onBomKeyChange(selectedMaterialIndex, bomKey)
                }
                onMaterialChange={(selectedMaterialOption) =>
                  onMaterialChange(selectedMaterialIndex, selectedMaterialOption)
                }
                onSplitLoadMaterialsSelected={(materials) =>
                  onSplitLoadMaterialsSelected(selectedMaterialIndex, materials)
                }
                onTimeSelectorTrack={partial(
                  formChangeHandlers.onTimeSelectorTrackApply,
                  selectedMaterialIndex,
                  editorActivationTimeStamp,
                  isSlotsManagementEnabled,
                  orderFields[selectedMaterialIndex].id
                )}
                onDeleteMaterial={() => removeCollection(selectedMaterialIndex)}
                onStatusChange={handleStatusChange}
                onFormValidation={(validity: OrdersFormValidityState) => {
                  const updatedOrdersFormValidity = [...ordersFormValidity]
                  updatedOrdersFormValidity[validity.index] = validity
                  setOrdersFormValidity(updatedOrdersFormValidity)
                }}
                setOrderWindowStatus={setOrderWindowStatus}
                slots={slots}
                setSlots={setSlots}
              />
            )
          )}
        </div>
      </Paper>
    </Grid>
  )
}
